/**
 * Spec 035 v0.4: the company of agents — one tree of units. Its types and its requests live
 * here, apart from the shared api modules: «Отделы» grows step by step (orders, events,
 * decisions, budgets), and each step adds to this file only.
 */
import { api } from "@/api/client";
import type { AgentAction, HelperMode, WordLine } from "@/api/types";

/** A label only: «департамент», «отдел», «сектор», «группа». */
export type UnitKind = "division" | "department" | "section" | "group";
/** Spec 042: «helper» is placed by the server, with its staff member; people place the staff. */
export type UnitRole = "leader" | "group" | "helper";
export type UnitStaffRole = "leader" | "group";
export type UnitRunState = "active" | "paused" | "stopped" | "never";

/** Spec 042: a staff member's helper, in the unit with it — a card of its own. */
export interface UnitHelperOut {
  agent_id: string;
  name: string;
  purpose_md: string | null;
  helper_mode: HelperMode;
  version: number;
  run_state: UnitRunState;
  run_last_action_at: string | null;
  archived: boolean;
  lessons?: number;
}

export interface UnitMemberOut {
  agent_id: string;
  name: string;
  purpose_md: string | null;
  /** The staff only: a staff member's helpers come under it, in `helpers`. */
  role: UnitStaffRole;
  position: number;
  run_state: UnitRunState;
  run_last_action_at: string | null;
  archived: boolean;
  /** «ждёт запуска»: orders or mail for it, and it does not run. */
  waits_launch: boolean;
  /** Spec 037: the instruction's sections its setup lacks, and its active lessons out of 20. */
  instruction_missing?: string[];
  lessons?: number;
  /** Spec 042: its helpers, under it. */
  helpers?: UnitHelperOut[];
}

export interface UnitPersonOut {
  id: string;
  name: string;
}

export interface UnitService {
  key: string;
  title: string;
  input_md: string | null;
  output_md: string | null;
  /** Who of the unit does it; null — its head takes it. */
  agent_id: string | null;
}

export interface UnitServiceOut extends UnitService {
  agent_name: string | null;
}

export interface DepartmentOut {
  id: string;
  name: string;
  purpose_md: string | null;
  version: number;
  parent_id: string | null;
  kind: UnitKind;
  depth: number;
  /** Names from the top unit to this one. */
  path: string[];
  children_ids: string[];
  head: UnitMemberOut | null;
  leader: UnitMemberOut | null;
  members: UnitMemberOut[];
  /** A top unit's own people; a nested unit shows its top unit's. */
  people: UnitPersonOut[];
  people_inherited: boolean;
  services: UnitServiceOut[];
  running: number;
  /** Spec 042: the staff's helpers, counted apart from the staff. */
  helpers?: number;
  /** Orders of this unit not closed yet. */
  open_orders: number;
  created_by: string;
  via_agent: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** «Без отдела»: a live card in no unit; it is not launched until placed. */
export interface UnplacedAgentOut {
  agent_id: string;
  name: string;
  purpose_md: string | null;
  run_state: UnitRunState;
  run_last_action_at: string | null;
}

export interface UnitMemberIn {
  agent_id: string;
  role: UnitStaffRole;
}

export interface DepartmentCreateIn {
  name: string;
  purpose_md?: string | null;
  kind?: UnitKind;
  parent_id?: string | null;
  members?: UnitMemberIn[];
  people_ids?: string[] | null;
  services?: UnitService[];
}

/** Only what is sent changes; `members`, `people_ids`, `services` replace the whole list;
 * `parent_id: null` makes it a top unit. */
export type DepartmentPatch = Partial<Omit<DepartmentCreateIn, "people_ids">> & {
  people_ids?: string[];
};

/** A person creates, changes and moves units here; archiving and restoring are a person's only. */
export const Departments = {
  list: (include_archived = false) =>
    api<DepartmentOut[]>(`/api/v1/departments${include_archived ? "?include_archived=true" : ""}`),
  get: (id: string) => api<DepartmentOut>(`/api/v1/departments/${id}`),
  /** «Без отдела»: cards to be placed; they are not launched until then. */
  unplaced: () => api<UnplacedAgentOut[]>("/api/v1/departments/unplaced"),
  /** The form's own key: a resend after a lost reply returns the first unit, not a second one. */
  create: (body: DepartmentCreateIn, idempotencyKey?: string) =>
    api<DepartmentOut>("/api/v1/departments", { method: "POST", body, idempotent: idempotencyKey ?? true }),
  update: (id: string, body: DepartmentPatch & { version: number }) =>
    api<DepartmentOut>(`/api/v1/departments/${id}`, { method: "PATCH", body }),
  /** 409 `has_live_children` while units sit inside. */
  archive: (id: string, version: number) =>
    api<DepartmentOut>(`/api/v1/departments/${id}/archive`, { method: "POST", body: { version }, idempotent: true }),
  /** 409 `parent_archived` while the unit above is in the archive. */
  restore: (id: string, version: number) =>
    api<DepartmentOut>(`/api/v1/departments/${id}/restore`, { method: "POST", body: { version }, idempotent: true }),
};

// ── Step 2: orders, mail and the conversation of a top unit ──────────────────

export type OrderState = "accepted" | "working" | "input_required" | "done" | "rejected" | "cancelled";
export type MessageKind = "message" | "question" | "answer" | "report";

/** A person or an agent card. */
export interface Who {
  kind: "person" | "agent";
  id: string | null;
  name: string;
}

export interface OrderOut {
  id: string;
  number: number;
  unit_id: string;
  unit_name: string;
  assignee: Who;
  given_by: Who;
  via_agent: boolean;
  parent_order_id: string | null;
  parent_number: number | null;
  waits_for_order_id: string | null;
  waits_for_number: number | null;
  service_key: string | null;
  title: string;
  body_md: string | null;
  expected_md: string;
  urgent: boolean;
  state: OrderState;
  reason_md: string | null;
  result_md: string | null;
  done_at: string | null;
  version: number;
  /** A part whose other part is not done yet. */
  waiting: boolean;
  /** «ждёт запуска»: its doer does not run now. */
  waits_launch: boolean;
  parts: number;
  /** Spec 045: what it is worked on with; with either, it gets a launch of its own. */
  model?: string | null;
  effort?: Effort | null;
  created_at: string;
  updated_at: string;
}

/** Spec 045: Claude Code's --effort. */
export type Effort = "low" | "medium" | "high" | "xhigh" | "max";
export const ORDER_MODELS = ["opus", "sonnet", "haiku"] as const;
export const ORDER_EFFORTS: Effort[] = ["low", "medium", "high", "xhigh", "max"];

/** «opus · high»; empty when the card decides. */
export const orderSettings = (order: Pick<OrderOut, "model" | "effort">): string =>
  [order.model, order.effort].filter(Boolean).join(" · ");

export interface MessageOut {
  id: string;
  unit_id: string | null;
  to_agent: Who | null;
  order_id: string | null;
  order_number: number | null;
  author: Who;
  via_agent: boolean;
  kind: MessageKind;
  body_md: string;
  /** A top head's word that went to Telegram at once. */
  urgent: boolean;
  reply_to_id: string | null;
  read_at: string | null;
  created_at: string;
}

export interface OrderDetailOut extends OrderOut {
  parts_list: OrderOut[];
  messages: MessageOut[];
}

export interface PersonOrderIn {
  title: string;
  body_md?: string | null;
  expected_md: string;
  urgent?: boolean;
  /** Spec 045: empty — the card's model, Claude Code's default effort. */
  model?: string | null;
  effort?: Effort | null;
}

/** People order the head of a top unit and talk with it; they read every unit's board. */
export const UnitWork = {
  board: (unitId: string) => api<OrderOut[]>(`/api/v1/departments/${unitId}/orders`),
  /** 409 `not_top_unit`, `no_head`; 403 `not_your_unit`. */
  give: (unitId: string, body: PersonOrderIn, idempotencyKey?: string) =>
    api<OrderOut>(`/api/v1/departments/${unitId}/orders`, { method: "POST", body, idempotent: idempotencyKey ?? true }),
  order: (orderId: string) => api<OrderDetailOut>(`/api/v1/departments/orders/${orderId}`),
  cancel: (orderId: string, version: number, reason_md?: string | null) =>
    api<OrderOut>(`/api/v1/departments/orders/${orderId}/cancel`, {
      method: "POST",
      body: { version, reason_md: reason_md ?? null },
      idempotent: true,
    }),
  conversation: (unitId: string) => api<MessageOut[]>(`/api/v1/departments/${unitId}/conversation`),
  write: (unitId: string, body: { body_md: string; kind?: "message" | "question"; order_id?: string | null }, idempotencyKey?: string) =>
    api<MessageOut>(`/api/v1/departments/${unitId}/conversation`, { method: "POST", body, idempotent: idempotencyKey ?? true }),
};

// ── Step 3: how a unit's head is launched ────────────────────────────────────

export type LaunchMode = "manual" | "auto" | "schedule";

export interface LaunchOut {
  unit_id: string;
  mode: LaunchMode;
  /** «09:00» — Yekaterinburg time, every day. */
  times: string[];
  device: { id: string; name: string; owner: string; online: boolean; revoked: boolean } | null;
  set_by: string | null;
  fired_at: string | null;
  /** Why the last automatic launch did not go. */
  note: string | null;
  note_at: string | null;
  /** 0 while never set: the unit is launched by hand. */
  version: number;
}

export interface LaunchResult {
  agent_id: string;
  name: string;
  role: "head" | "staff";
  result: "launched" | "running" | "offline" | "refused";
  message: string | null;
}

export const UnitLaunches = {
  get: (unitId: string) => api<LaunchOut>(`/api/v1/departments/${unitId}/launch`),
  /** Only one's own PC: the launch goes in its owner's name (403 `not_device_owner`). */
  set: (unitId: string, body: { mode: LaunchMode; times: string[]; device_id: string | null; version: number | null }) =>
    api<LaunchOut>(`/api/v1/departments/${unitId}/launch`, { method: "PUT", body }),
  /** «Запустить подразделение»: the head and the staff with work; 409 `no_device` without a PC. */
  now: (unitId: string, deviceId: string | null) =>
    api<LaunchResult[]>(`/api/v1/departments/${unitId}/launch`, { method: "POST", body: { device_id: deviceId } }),
};

// ── Step 4: events and subscriptions ─────────────────────────────────────────

export interface UnitEventOut {
  id: string;
  unit_id: string;
  unit_name: string;
  /** A short latin key: «case-accepted». */
  kind: string;
  title: string;
  body_md: string | null;
  order_id: string | null;
  order_number: number | null;
  author: Who | null;
  created_at: string;
}

export interface UnitSubscriptionOut {
  id: string;
  unit_id: string;
  unit_name: string;
  source_unit_id: string;
  source_unit_name: string;
  /** null: every event of the source. */
  kind: string | null;
  created_at: string;
}

/** Heads subscribe their units themselves; people read. */
export const UnitEvents = {
  feed: (unitId: string) => api<UnitEventOut[]>(`/api/v1/departments/${unitId}/events`),
  subscriptions: (unitId: string) =>
    api<{ listens: UnitSubscriptionOut[]; listeners: UnitSubscriptionOut[] }>(`/api/v1/departments/${unitId}/subscriptions`),
};

// ── Step 5: decisions for people ─────────────────────────────────────────────

export interface UnitDecisionOut {
  id: string;
  number: number;
  unit_id: string;
  unit_name: string;
  asked_by: Who;
  order_id: string | null;
  order_number: number | null;
  /** «budget»: «добавить ещё?» — its first option adds the month's limit again. */
  topic: string;
  question: string;
  context_md: string | null;
  options: string[];
  urgent: boolean;
  state: "open" | "answered" | "cancelled";
  answer: string | null;
  option: number | null;
  decided_by: Who | null;
  via_agent: boolean;
  decided_at: string | null;
  created_at: string;
  /** Spec 037: «instruction» — a new instruction for a staff member, with what it changes. */
  instruction?: InstructionView | null;
}

export interface InstructionView {
  agent_id: string;
  agent_name: string;
  base_version: number;
  current_version: number;
  /** The card changed since: accepting writes nothing. */
  stale: boolean;
  lines: { sign: "+" | "-"; text: string; mark: [number, number] | null }[];
  lessons: string[];
  /** Sections the new text still lacks. */
  missing: string[];
}

/** Only the heads of top units ask; one of the unit's people answers once. */
export const UnitDecisions = {
  open: () => api<UnitDecisionOut[]>("/api/v1/departments/decisions/open"),
  ofUnit: (unitId: string) => api<UnitDecisionOut[]>(`/api/v1/departments/${unitId}/decisions`),
  /** 409 `decision_closed`: someone answered first. */
  answer: (id: string, body: { option: number } | { answer: string }) =>
    api<UnitDecisionOut>(`/api/v1/departments/decisions/${id}/answer`, { method: "POST", body, idempotent: true }),
};

// ── Step 6: budgets ──────────────────────────────────────────────────────────

export interface SpendLine {
  id: string;
  name: string;
  tokens: number;
}

export interface BudgetOut {
  unit_id: string;
  unit_name: string;
  /** «2026-10», Yekaterinburg. */
  month: string;
  tokens_per_month: number | null;
  added: number;
  /** Its staff's and every unit inside it. */
  spent: number;
  over: boolean;
  by_agent: SpendLine[];
  by_unit: SpendLine[];
  version: number;
}

/** Money is people's: the limit is set in the app only. */
export const UnitBudgets = {
  all: () => api<BudgetOut[]>("/api/v1/departments/budgets/month"),
  get: (unitId: string) => api<BudgetOut>(`/api/v1/departments/${unitId}/budget`),
  set: (unitId: string, tokens_per_month: number | null, version: number | null) =>
    api<BudgetOut>(`/api/v1/departments/${unitId}/budget`, { method: "PUT", body: { tokens_per_month, version } }),
};

// ── Spec 037: the company document and the agents' lessons ───────────────────

export interface CompanyBriefOut {
  version: number;
  text_md: string;
  author: Who;
  via_agent: boolean;
  user_confirmation: string | null;
  created_at: string;
}

export type LessonSource = "head" | "person" | "self";

export interface LessonOut {
  id: string;
  agent_id: string;
  agent_name: string;
  text_md: string;
  source: LessonSource;
  by: Who | null;
  via_agent: boolean;
  order_id: string | null;
  order_number: number | null;
  repeats: number;
  state: "active" | "archived" | "deleted";
  /** The card version whose instruction took it in. */
  in_version: number | null;
  created_at: string;
  closed_at: string | null;
}

/** «О компании для сотрудников»: one document, every agent of the company gets it. */
export const CompanyBrief = {
  get: () => api<CompanyBriefOut | null>("/api/v1/departments/company/brief"),
  /** 409 `brief_stale`: someone changed it since `version`. */
  set: (text_md: string, version: number | null) =>
    api<CompanyBriefOut>("/api/v1/departments/company/brief", { method: "PUT", body: { text_md, version } }),
  versions: () => api<CompanyBriefOut[]>("/api/v1/departments/company/versions"),
};

/** An agent's journal of lessons: people write and delete; the agent works by the active ones. */
export const Lessons = {
  of: (agentId: string, state?: "active" | "archived") =>
    api<LessonOut[]>(`/api/v1/departments/company/lessons?agent_id=${agentId}${state ? `&state=${state}` : ""}`),
  /** 409 `journal_full`: 20 active already. */
  write: (agentId: string, text_md: string) =>
    api<LessonOut>("/api/v1/departments/company/lessons", {
      method: "POST",
      body: { agent_id: agentId, text_md },
      idempotent: true,
    }),
  remove: (lessonId: string) =>
    api<LessonOut>(`/api/v1/departments/company/lessons/${lessonId}`, { method: "DELETE" }),
};

// ── Spec 038: the company's tools ────────────────────────────────────────────

export interface ToolModel {
  /** The service's path: «higgsfield-ai/soul/v2/standard». */
  path: string;
  title: string;
  notes_md?: string | null;
}

export interface CompanyToolOut {
  id: string;
  key: string;
  title: string;
  kind: "higgsfield";
  notes_md: string | null;
  models: ToolModel[];
  /** A key is bound: the tool works. */
  has_key: boolean;
  /** The bound secret: the form starts on it. */
  secret_id: string | null;
  secret_name: string | null;
  key_bound_by: Who | null;
  created_by: Who;
  via_agent: boolean;
  archived_at: string | null;
  version: number;
  created_at: string;
  month_calls: number;
  month_done: number;
}

export type ToolCallState = "queued" | "in_progress" | "completed" | "failed" | "nsfw" | "canceled" | "error";

export interface ToolFileOut {
  n: number;
  kind: string;
  content_type: string | null;
  bytes: number | null;
  kept: boolean;
  url: string | null;
}

export interface ToolCallOut {
  id: string;
  tool_key: string;
  tool_title: string;
  agent: Who;
  order_id: string | null;
  order_number: number | null;
  model: string;
  input: Record<string, unknown>;
  state: ToolCallState;
  error: string | null;
  files: ToolFileOut[];
  created_at: string;
  finished_at: string | null;
}

export interface CompanyToolIn {
  key: string;
  title: string;
  notes_md?: string | null;
  models: ToolModel[];
}

/** Partners keep the registry; only the person who stored a key binds it. */
export const CompanyTools = {
  list: (includeArchived = false) =>
    api<CompanyToolOut[]>(`/api/v1/departments/company/tools${includeArchived ? "?include_archived=true" : ""}`),
  create: (body: CompanyToolIn) =>
    api<CompanyToolOut>("/api/v1/departments/company/tools", { method: "POST", body, idempotent: true }),
  update: (id: string, body: Partial<Omit<CompanyToolIn, "key">> & { version: number; archived?: boolean }) =>
    api<CompanyToolOut>(`/api/v1/departments/company/tools/${id}`, { method: "PATCH", body }),
  /** 403 `key_not_yours`: the key is bound by the person who stored it. */
  bindKey: (id: string, version: number, secretId: string | null) =>
    api<CompanyToolOut>(`/api/v1/departments/company/tools/${id}/key`, {
      method: "PUT",
      body: { version, secret_id: secretId },
    }),
  /** Does the service take the key? Nothing is generated or spent. */
  check: (id: string) =>
    api<{ ok: boolean; message: string }>(`/api/v1/departments/company/tools/${id}/check`, { method: "POST" }),
  calls: (params: { tool_id?: string; agent_id?: string; order_id?: string } = {}) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]);
    return api<ToolCallOut[]>(`/api/v1/departments/company/tools/calls${query.size ? `?${query}` : ""}`);
  },
};

// ── Spec 039: the company as one view ────────────────────────────────────────

/** here — on the PC that asks (the app only); running — works, `pc` says where; other_pc — on
 * another PC; pc_lost — its PC went silent; waits_launch — has work, does not run; off. */
export type StaffStatus = "here" | "other_pc" | "running" | "pc_lost" | "waits_launch" | "off";

export interface PcRef {
  id: string;
  name: string;
  owner_name: string;
  mine: boolean;
}

export interface ViewStaff {
  agent_id: string;
  name: string;
  unit_id: string;
  role: "head" | "staff";
  version: number;
  status: StaffStatus;
  pc: PcRef | null;
  last_action_at: string | null;
  /** Not working: since when; null while it works or when it never ran. */
  inactive_since: string | null;
  /** Minutes its console was open; null — no data, never zero. */
  worked_today_min: number | null;
  worked_week_min: number | null;
  order: { id: string; number: number; title: string; state: string } | null;
  /** It asked people a decision that is still open. */
  waits_people: boolean;
  /** Spec 040: what its run waits for from people — permission, question, right, limit — and since when. */
  waits_for?: string | null;
  waits_since?: string | null;
  spent_month: number;
}

export interface ViewUnit {
  id: string;
  name: string;
  kind: UnitKind;
  parent_id: string | null;
  depth: number;
  path: string[];
  purpose_md: string | null;
  children_ids: string[];
  /** Its head first, then its staff. */
  staff_ids: string[];
  open_orders: number;
  open_decisions: number;
  /** This month, nested units included. */
  spent_month: number;
  limit_month: number | null;
  over: boolean;
}

export interface ViewPc extends PcRef {
  online: boolean;
  last_seen_at: string | null;
  working: number;
}

export interface CompanyViewOut {
  at: string;
  month: string;
  this_pc_id: string | null;
  units: ViewUnit[];
  staff: ViewStaff[];
  pcs: ViewPc[];
  unplaced: { agent_id: string; name: string }[];
}

/** Who works where now, since when, and what each unit spent — one answer for «Отделы». */
export const CompanyView = {
  get: () => api<CompanyViewOut>("/api/v1/company/view"),
};

// ── Spec 055: the unit's kit ─────────────────────────────────────────────────

/** A shared note of the kit: read, or read and append entries. */
export interface KitNoteGrant {
  note_id: string;
  append: boolean;
}

/** «Находки» no higher than report, «Заказы клиентов» no higher than own; the risky rights are
 * a card's alone and never enter a kit (422 `kit_risky_right`). */
export type KitRadar = "none" | "read" | "report";
export type KitOrders = "none" | "own";

export interface UnitKit {
  notes: KitNoteGrant[];
  skills: string[];
  actions: AgentAction[];
  projects: string[];
  radar: KitRadar;
  orders: KitOrders;
}

export const EMPTY_KIT: UnitKit = { notes: [], skills: [], actions: [], projects: [], radar: "none", orders: "none" };

export interface KitVersionOut {
  id: string;
  unit_id: string;
  version: number;
  kit: UnitKit;
  author: Who;
  via_agent: boolean;
  user_confirmation: string | null;
  approved: boolean;
  approvals: { person: Who; approved_at: string }[];
  created_at: string;
}

/** What a unit above gives: its kit in force, with where it comes from. */
export interface InheritedKitOut {
  unit_id: string;
  unit_name: string;
  version: number;
  kit: UnitKit;
}

export interface KitOut {
  unit_id: string;
  unit_name: string;
  /** The latest version's kit; empty while there is none. */
  own: UnitKit;
  /** The latest version; 0 while there is none. */
  version: number;
  /** The version in force; null — the unit is as without a kit. */
  approved_version: number | null;
  /** The latest version waits for a «да». */
  pending: boolean;
  latest: KitVersionOut | null;
  in_force: KitVersionOut | null;
  /** The units above, the nearest first. */
  inherited: InheritedKitOut[];
  /** Cards that get it: this unit's and the nested units'. */
  agents: number;
  /** Who may approve it: the top unit's people. */
  people: Who[];
  /** Step 3: names of the notes and projects its kits name. */
  note_titles?: Record<string, string>;
  project_titles?: Record<string, string>;
}

export interface KitDiffOut {
  unit_id: string;
  /** 0 is the empty kit. */
  from_version: number;
  to_version: number;
  lines: WordLine[];
}

/** The unit's kit: people of the unit's tree save a version, any one of the top unit's people
 * approves it with one «да»; a new version changes no card until then. */
export const UnitKits = {
  get: (unitId: string) => api<KitOut>(`/api/v1/departments/${unitId}/kit`),
  /** 409 `kit_stale`: a newer version than `version`; 422 `kit_risky_right`, `kit_invalid`. */
  set: (unitId: string, body: UnitKit & { version: number }) =>
    api<KitOut>(`/api/v1/departments/${unitId}/kit`, { method: "PUT", body }),
  versions: (unitId: string) => api<KitVersionOut[]>(`/api/v1/departments/${unitId}/kit/versions`),
  /** Default: from the version in force (0 — the empty kit) to the latest. */
  diff: (unitId: string, fromVersion?: number, toVersion?: number) => {
    const query = new URLSearchParams();
    if (fromVersion !== undefined) query.set("from_version", String(fromVersion));
    if (toVersion !== undefined) query.set("to_version", String(toVersion));
    return api<KitDiffOut>(`/api/v1/departments/${unitId}/kit/diff${query.size ? `?${query}` : ""}`);
  },
  /** One «да»: the latest version (409 `kit_stale` when a newer one came) comes into force. */
  approve: (unitId: string, version: number) =>
    api<KitOut>(`/api/v1/departments/${unitId}/kit/approve`, { method: "POST", body: { version }, idempotent: true }),
};

// ── Spec 055, step 3: «+ Добавить» searches a page at a time ─────────────────────

export interface FoundNote {
  id: string;
  title: string;
}

export interface FoundSkill {
  slug: string;
  name: string;
  /** SkillSpector's verdict; null — no record. */
  verdict: "safe" | "accepted" | "caution" | "blocked" | null;
}

export interface SearchPage<T> {
  items: T[];
  /** null — the last page. */
  next_offset: number | null;
}

const searchQuery = (q: string, offset: number) => new URLSearchParams({ q, offset: String(offset) }).toString();

/** Shared notes by title and registry skills by slug or name — for the screens only. */
export const KitSearch = {
  notes: (q: string, offset = 0) =>
    api<SearchPage<FoundNote>>(`/api/v1/departments/kit-search/notes?${searchQuery(q, offset)}`),
  skills: (q: string, offset = 0) =>
    api<SearchPage<FoundSkill>>(`/api/v1/departments/kit-search/skills?${searchQuery(q, offset)}`),
};
